import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, Ruler, Truck, Star, ShoppingBag, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * SEO landing page targeting the search intent
 * "Torwarthandschuhe Damen (Schweiz)" — currently dominated by Ochsner Sport
 * and 11teamsports because 4keeperz has no dedicated women page.
 *
 * Route: /torwarthandschuhe-damen
 * Angles: Female sizing 7-8, Breathapren fit for narrower hands,
 * professional grip identical to men's gloves.
 */
const SIZE_TABLE = [
  { size: "6", cm: "17–18 cm", label: "Junge Damen / Jugend", note: "Bald verfügbar" },
  { size: "7", cm: "18–19 cm", label: "Damen Standard", note: "Verfügbar" },
  { size: "8", cm: "20–21 cm", label: "Damen L / Junior", note: "Verfügbar" },
];

const WOMEN_FAQS = [
  {
    q: "Welche Größe brauchen Damen bei 4keeperz Torwarthandschuhen?",
    a: "Für erwachsene Damen ist Größe 7 (Handumfang 18–19 cm) und Größe 8 (20–21 cm) die häufigste Wahl. Miss den Umfang deiner dominanten Hand direkt unterhalb der Knöchel, ohne Daumen. Im Zweifel die kleinere Größe wählen — Latex dehnt sich beim Tragen minimal aus. Größe 6 für zierlichere Damen kommt in Kürze.",
  },
  {
    q: "Sind 4keeperz Handschuhe unisex geschnitten?",
    a: "Ja. Mamba und Panther Edition sind unisex und werden über die Handgrösse skaliert, nicht über Geschlecht. Der Breathapren-Body sitzt bei schmaleren Damenhänden dank Negative Cut und Dual Stripe Verschluss besonders eng — direktes Ballgefühl ohne Verrutschen.",
  },
  {
    q: "Warum kein spezielles 'Damen'-Modell mit Rüschen oder pinkem Design?",
    a: "Weil Torhüter-Performance nichts mit Farbrasier zu tun hat. Damen und Herren brauchen dasselbe: German Latex, Grip, Passform. Unsere Green Mamba und Black Panther sind für alle Keeperz gebaut — kompromisslos.",
  },
  {
    q: "Bekomme ich als Vereinsspielerin einen Rabatt?",
    a: "Ja. Wenn dein Verein mit uns kooperiert (aktuell FC Uitikon), erhältst du automatisch 15% Rabatt via Vereinslink. Frag bei deiner Trainerin nach, oder schreib uns direkt an info@4keeperz.ch für eine Team-Bestellung.",
  },
];

const TorwarthandschuheDamen = () => {
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState({}); // {slug: size}

  useEffect(() => {
    document.title =
      "Torwarthandschuhe Damen — 4keeperz Schweiz | German Latex Pro Grip";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Torwarthandschuhe für Damen — 4keeperz Swiss Brand. Größe 7 & 8, 4mm German Giga Grip Latex, Breathapren Body, Negative Cut. Ab CHF 99. Versand CH & FL, gratis ab CHF 80."
      );
    }
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Inject Product + Breadcrumb schema.org JSON-LD for this page
  useEffect(() => {
    if (!products.length) return;
    const gloves = products.filter((p) => ["mamba-edition", "panther-edition"].includes(p.slug));
    const scriptId = "damen-page-schema";
    document.getElementById(scriptId)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://4keeperz.ch/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Torwarthandschuhe Damen",
              item: "https://4keeperz.ch/torwarthandschuhe-damen",
            },
          ],
        },
        {
          "@type": "CollectionPage",
          name: "Torwarthandschuhe für Damen",
          url: "https://4keeperz.ch/torwarthandschuhe-damen",
          description:
            "4keeperz Torwarthandschuhe für Damen — Größe 7 und 8. German Giga Grip Latex, Breathapren Body, Negative Cut.",
          isPartOf: { "@type": "WebSite", url: "https://4keeperz.ch/" },
          hasPart: gloves.map((p) => ({
            "@type": "Product",
            name: p.name,
            image: p.image ? `https://4keeperz.ch${p.image}` : undefined,
            offers: {
              "@type": "Offer",
              priceCurrency: "CHF",
              price: String(p.price_chf),
              availability: "https://schema.org/InStock",
              url: `https://4keeperz.ch/torwarthandschuhe-damen#${p.slug}`,
            },
          })),
        },
        {
          "@type": "FAQPage",
          mainEntity: WOMEN_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(scriptId)?.remove();
  }, [products]);

  const gloves = products.filter((p) => ["mamba-edition", "panther-edition"].includes(p.slug));

  const handleAddToCart = (product) => {
    const size = selectedSize[product.slug];
    if (!size) {
      toast.error("Bitte Größe wählen");
      return;
    }
    cart.addItem(
      { slug: product.slug, name: product.name, image: product.image, price_chf: product.price_chf },
      size
    );
    toast.success(`${product.name} · Größe ${size} in den Warenkorb`);
  };

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="damen-page">
      {/* HERO */}
      <section
        className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 700px 500px at 85% 30%, rgba(0,166,81,0.14), transparent 60%), radial-gradient(ellipse 500px 400px at 10% 90%, rgba(0,166,81,0.08), transparent 60%), #050505",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6">
          <nav
            className="flex items-center gap-2 text-xs font-condensed uppercase tracking-widest text-white/50 mb-6"
            data-testid="damen-breadcrumb"
          >
            <Link to="/" className="hover:text-[#00A651]">
              4keeperz
            </Link>
            <span>/</span>
            <span className="text-white/70">Damen</span>
          </nav>

          <div className="inline-flex items-center gap-2.5 border border-[#00A651]/40 bg-[#00A651]/5 px-4 py-2 mb-7">
            <span className="w-1.5 h-1.5 bg-[#00A651] rounded-full animate-pulse" />
            <span className="font-condensed uppercase tracking-[0.3em] text-[11px] text-[#00A651]">
              Größe 7 & 8 verfügbar · Größe 6 in Kürze
            </span>
          </div>

          <h1 className="font-brush leading-[0.95] text-5xl md:text-7xl mb-6 max-w-3xl">
            Torwarthandschuhe
            <span className="block text-[#00A651]">für Damen.</span>
          </h1>
          <p className="text-white/75 max-w-2xl text-base md:text-lg leading-relaxed mb-8">
            Kein Damen-Marketing. Kein Rüschen-Design. Nur die gleichen{" "}
            <strong className="text-white">4mm German Giga Grip Latex</strong>-Handschuhe wie die
            Profis tragen — in Größen, die zu deiner Hand passen. Von einem aktiven Torwart in der
            Schweiz gebaut. Für alle, die den Ball halten wollen.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            <a
              href="#collection"
              className="inline-flex items-center gap-3 bg-[#00A651] hover:bg-[#00C961] text-black font-condensed uppercase tracking-[0.2em] text-[13px] px-7 py-3.5 transition-colors"
              data-testid="damen-hero-shop-btn"
            >
              <ShoppingBag className="w-4 h-4" />
              Jetzt shoppen
            </a>
            <a
              href="#groesse"
              className="inline-flex items-center gap-3 border border-white/25 hover:border-[#00A651] hover:text-[#00A651] text-white font-condensed uppercase tracking-[0.2em] text-[13px] px-7 py-3.5 transition-colors"
              data-testid="damen-hero-size-btn"
            >
              <Ruler className="w-4 h-4" />
              Größe finden
            </a>
          </div>

          <div className="flex flex-wrap gap-6 pt-6 border-t border-white/10 max-w-2xl">
            {[
              { icon: Check, label: "4mm German Latex" },
              { icon: Check, label: "Breathapren Body" },
              { icon: Truck, label: "Gratis ab CHF 80" },
              { icon: Star, label: "5.0 Google" },
            ].map((f) => (
              <div
                key={f.label}
                className="flex items-center gap-2 font-condensed uppercase tracking-[0.15em] text-xs text-white/60"
              >
                <f.icon className="w-3.5 h-3.5 text-[#00A651]" />
                {f.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIZE GUIDE FIRST — This is where women drop off */}
      <section id="groesse" className="py-16 md:py-24 bg-black border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="font-condensed uppercase tracking-[0.3em] text-[11px] text-[#00A651] mb-3">
                Größentabelle · Damen
              </div>
              <h2 className="font-brush text-4xl md:text-5xl leading-tight">
                Deine Hand.
                <span className="text-[#00A651]"> Deine Größe.</span>
              </h2>
            </div>
            <p className="text-white/60 max-w-md text-sm leading-relaxed">
              Miss den Umfang deiner dominanten Hand ohne Daumen, direkt unterhalb der Knöchel.
              Wähle im Zweifel die <strong className="text-white">kleinere Größe</strong> — Latex
              dehnt sich minimal beim Tragen.
            </p>
          </div>

          <div className="border border-white/10 overflow-hidden">
            <table className="w-full" data-testid="damen-size-table">
              <thead className="bg-[#0A0A0A]">
                <tr className="text-left">
                  <th className="px-5 py-4 font-condensed uppercase tracking-widest text-[11px] text-white/50">
                    Größe
                  </th>
                  <th className="px-5 py-4 font-condensed uppercase tracking-widest text-[11px] text-white/50">
                    Handumfang
                  </th>
                  <th className="px-5 py-4 font-condensed uppercase tracking-widest text-[11px] text-white/50">
                    Passform
                  </th>
                  <th className="px-5 py-4 font-condensed uppercase tracking-widest text-[11px] text-white/50 text-right">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {SIZE_TABLE.map((row) => {
                  const soon = row.note === "Bald verfügbar";
                  return (
                    <tr
                      key={row.size}
                      className={`border-t border-white/5 ${soon ? "opacity-60" : ""}`}
                      data-testid={`damen-size-row-${row.size}`}
                    >
                      <td className="px-5 py-4">
                        <span className="font-brush text-3xl text-[#00A651]">{row.size}</span>
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-white/80">{row.cm}</td>
                      <td className="px-5 py-4 text-sm text-white/70">{row.label}</td>
                      <td className="px-5 py-4 text-right">
                        <span
                          className={`inline-block font-condensed uppercase tracking-widest text-[10px] px-2 py-1 ${
                            soon
                              ? "border border-white/20 text-white/50"
                              : "bg-[#00A651]/15 text-[#00A651] border border-[#00A651]/30"
                          }`}
                        >
                          {row.note}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-start gap-3 border border-white/10 bg-[#0A0A0A] p-4">
            <Info className="w-5 h-5 text-[#00A651] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-white/70 leading-relaxed">
              <strong className="text-white">Größe 6 kommt in Kürze</strong> für zierliche Damen und
              Jugendliche. Schreib uns kurz an{" "}
              <a href="mailto:info@4keeperz.ch?subject=Waitlist Größe 6" className="text-[#00A651] hover:underline">
                info@4keeperz.ch
              </a>{" "}
              — wir melden uns, sobald sie im Shop ist.
            </p>
          </div>
        </div>
      </section>

      {/* COLLECTION — Same products, women-first framing */}
      <section id="collection" className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="font-condensed uppercase tracking-[0.3em] text-[11px] text-[#00A651] mb-3">
            Die Kollektion · Unisex Pro Quality
          </div>
          <h2 className="font-brush text-4xl md:text-5xl leading-tight mb-10">
            Zwei Editionen.
            <span className="text-[#00A651]"> Für alle Keeperz.</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {gloves.map((p) => (
              <article
                key={p.slug}
                id={p.slug}
                className="border border-white/10 bg-[#0A0A0A] p-6 group hover:border-[#00A651]/60 transition-colors"
                data-testid={`damen-product-${p.slug}`}
              >
                <div className="aspect-square bg-black mb-5 overflow-hidden">
                  <img
                    src={p.image?.startsWith("/") ? `${BACKEND}${p.image}` : p.image}
                    alt={`${p.name} · Torwarthandschuhe Damen · 4keeperz`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h3 className="font-brush text-3xl">{p.name}</h3>
                  <span className="font-display text-2xl text-[#00A651] whitespace-nowrap">
                    CHF {p.price_chf}
                  </span>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-5">
                  {p.slug === "mamba-edition"
                    ? "4mm German Giga Grip · maximaler Grip bei Nässe. Signature Green Colorway. Silikon-Punchingzone."
                    : "4mm German Contact Grip · robust und ausbalanciert für trockene Bedingungen. All-Black Design. Latex-Punchingzone."}
                </p>
                <div className="mb-4">
                  <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
                    Größe wählen (Damen)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["7", "8"].map((s) => {
                      const active = selectedSize[p.slug] === s;
                      return (
                        <button
                          key={s}
                          onClick={() => setSelectedSize((prev) => ({ ...prev, [p.slug]: s }))}
                          className={`w-11 h-11 border font-brush text-xl transition-colors ${
                            active
                              ? "border-[#00A651] bg-[#00A651] text-black"
                              : "border-white/20 hover:border-[#00A651]/60 text-white"
                          }`}
                          data-testid={`damen-size-${p.slug}-${s}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                    <div className="flex items-center gap-2 pl-3 border-l border-white/10">
                      <span className="font-condensed uppercase tracking-widest text-[10px] text-white/40">
                        Größer? 9–11 auf der{" "}
                        <Link to="/" className="text-[#00A651] hover:underline">
                          Startseite
                        </Link>
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleAddToCart(p)}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#00A651] hover:bg-[#00C961] text-black font-condensed uppercase tracking-[0.2em] text-[13px] py-3.5 transition-colors"
                  data-testid={`damen-add-${p.slug}`}
                >
                  <ShoppingBag className="w-4 h-4" />
                  In den Warenkorb
                </button>
              </article>
            ))}
          </div>

          {gloves.length === 0 && (
            <div className="text-center text-white/40 py-12">Produkte werden geladen…</div>
          )}
        </div>
      </section>

      {/* WHY — Different angle for women */}
      <section className="py-16 md:py-24 bg-black border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="font-condensed uppercase tracking-[0.3em] text-[11px] text-[#00A651] mb-3">
            Warum 4keeperz für Damen
          </div>
          <h2 className="font-brush text-4xl md:text-5xl leading-tight mb-10">
            Kein Kompromiss.
            <span className="text-[#00A651]"> Nur Passform.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Enge Passform",
                text: "Negative Cut + Dual Stripe Verschluss halten den Handschuh auch bei schmaleren Handgelenken bombenfest — kein Verrutschen, keine Falten.",
              },
              {
                title: "Gleiches Material wie Männer",
                text: "4mm German Giga Grip / Contact Grip Latex. Kein 'Damen-Latex', keine reduzierte Qualität. Grip ist Grip.",
              },
              {
                title: "Direkt vom Torwart",
                text: "Entwickelt von Segsan Gröschel (aktiver Keeper FC Wädenswil) mit Ex-Profi Ivan Bonfardin. Keine Konzern-Ware, kein Aufschlag.",
              },
            ].map((c) => (
              <div key={c.title} className="border border-white/10 bg-[#0A0A0A] p-6">
                <div className="w-8 h-8 border border-[#00A651] flex items-center justify-center mb-4">
                  <Check className="w-4 h-4 text-[#00A651]" />
                </div>
                <h3 className="font-brush text-2xl mb-3">{c.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — Women specific */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="font-condensed uppercase tracking-[0.3em] text-[11px] text-[#00A651] mb-3">
            FAQ · Damen
          </div>
          <h2 className="font-brush text-4xl md:text-5xl leading-tight mb-10">
            Häufige Fragen.
          </h2>
          <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/5">
            {WOMEN_FAQS.map((f, i) => (
              <details
                key={i}
                className="group p-5"
                data-testid={`damen-faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                  <span className="font-condensed uppercase tracking-widest text-sm text-white group-open:text-[#00A651]">
                    {f.q}
                  </span>
                  <span className="text-[#00A651] text-xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-sm text-white/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-3 font-condensed uppercase tracking-[0.2em] text-[13px] text-white/70 hover:text-[#00A651] transition-colors"
              data-testid="damen-to-home"
            >
              Alle FAQs & mehr auf der Startseite ansehen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TorwarthandschuheDamen;
